import { ChangeDetectorRef, computed, ElementRef, EventEmitter, inject, input, SimpleChanges, viewChild, } from '@angular/core';
import { BENCHMARK_COLOR, BENCHMARK_SERIES_ID, GENERIC_ERROR_MESSAGE, HIGHCHARTS_UNIVERSE_COLUMNRANGE_DEFAULT_OPTIONS, LATEST_CATEGORY_LABEL, NOT_RANKED_LABEL, TARGET_COLOR_INDICES, TARGET_COLORS, TARGET_SERIES_ID_PREFIX, UNIVERSE_BANDS, UniverseQuartileChartFrequency, } from '../../constants';
import { ChartDateZoom } from 'ChartApp/ts/enums/chart-common.enum';
import { UniverseQuartileChartService } from '../../services/universe-quartile-chart.service';
import { buildNoDataMessage, formatDate, formatValue, tooltipRow } from '../../utils';
import Highcharts from '../../highcharts';
import * as i0 from "@angular/core";
const _c0 = ["chart"];
function UniverseQuartileChartComponent_Conditional_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2)(1, "div", 6);
    i0.ɵɵtext(2, "Loading ...");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(3, "div", 7);
    i0.ɵɵelementEnd();
} }
function UniverseQuartileChartComponent_Conditional_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 3);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.errorMessage);
} }
function UniverseQuartileChartComponent_Conditional_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.noDataMessage);
} }
function UniverseQuartileChartComponent_Conditional_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "div", 5, 0);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("id", ctx_r0.chartId());
} }
/** Inputs that shape the request. Anything else is presentation and must not cost a round trip. */
const REQUEST_INPUTS = [
    'targetSecurities',
    'metric',
    'securitylistName',
    'securityGroup',
    'zoom',
    'frequency',
    'includeBenchmark',
];
/**
 *
 * Universe Quartile Chart Component
 *
 * Draws one metric's distribution across a universe of funds as a box per period, with each
 * target fund's own value plotted over it, and optionally the first target's benchmark.
 *
 * This component is parameters in, chart out: it draws the plot and nothing around it. It owns no
 * controls and renders no title, legend or universe framing -- a host wraps it with those -- which
 * is what lets the same chart be dropped into a report module or a dashboard panel with fixed
 * parameters. Anything that *changes* a parameter belongs to a host component, not here.
 *
 * Input Properties:
 *  targetSecurities: securities being compared against the universe; the endpoint accepts at most five
 *  metric: calc to plot
 *  securitylistName / securityGroup: the universe to summarize
 *  zoom / frequency: the timeframe, and how finely it is bucketed
 *  includeBenchmark: overlays the first target's benchmark as a separate set of points
 *  chartId: id put on the chart container element
 *  noDataHtml / chartOptions: presentation only, never refetch
 */
export class UniverseQuartileChartComponent {
    constructor() {
        this.chartElement = viewChild('chart', ...(ngDevMode ? [{ debugName: "chartElement" }] : []));
        this.targetSecurities = input([], ...(ngDevMode ? [{ debugName: "targetSecurities" }] : []));
        this.targetSecurityIds = computed(() => {
            const rawTargetSecIds = this.targetSecurities();
            return Array.isArray(rawTargetSecIds) ? rawTargetSecIds : rawTargetSecIds.split(',');
        }, ...(ngDevMode ? [{ debugName: "targetSecurityIds" }] : []));
        this.metric = input('', ...(ngDevMode ? [{ debugName: "metric" }] : []));
        this.securitylistName = input('', ...(ngDevMode ? [{ debugName: "securitylistName" }] : []));
        this.securityGroup = input('', ...(ngDevMode ? [{ debugName: "securityGroup" }] : []));
        this.zoom = input(ChartDateZoom.CHART_ZOOM_ONE_YEAR, ...(ngDevMode ? [{ debugName: "zoom" }] : []));
        this.frequency = input(UniverseQuartileChartFrequency.MONTHLY, ...(ngDevMode ? [{ debugName: "frequency" }] : []));
        this.includeBenchmark = input(false, ...(ngDevMode ? [{ debugName: "includeBenchmark" }] : []));
        this.chartId = input('', ...(ngDevMode ? [{ debugName: "chartId" }] : []));
        this.noDataHtml = input('', ...(ngDevMode ? [{ debugName: "noDataHtml" }] : []));
        this.chartOptions = input({}, ...(ngDevMode ? [{ debugName: "chartOptions" }] : []));
        /**
         * Emits on every settled load -- data, empty, or error -- so a host can react without
         * reaching into this component for its state.
         */
        this.chartDataHandler = new EventEmitter();
        this.chartData = null;
        this.chartParams = null;
        this.isLoading = false;
        this.errorMessage = '';
        this.chart = null;
        this.fetchSubscription = null;
        this.universeQuartileChartService = inject(UniverseQuartileChartService);
        this.changeDetector = inject(ChangeDetectorRef);
    }
    get isEmpty() {
        if (!this.chartData)
            return true;
        return !this.chartData.universe.boxes.length;
    }
    get noDataMessage() {
        return this.noDataHtml() || buildNoDataMessage(this.chartData);
    }
    /**
     * A metric with no history comes back as a single period. There is no timeline to draw, so
     * the chart falls back to one box on a category axis.
     */
    get isLatestMode() {
        return !!this.chartData && !this.chartData.metric.hasHistory;
    }
    ngOnChanges(changes) {
        // Fetch data only when relevant inputs have been updated
        const updatingInputs = Object.entries(changes).filter(([key, _]) => REQUEST_INPUTS.includes(key));
        if (updatingInputs.length &&
            // Exclude when ONLY frequency has updated on a latest calc. This prevents a request with same result
            // as frequency is ignored by latest calcs
            !(this.isLatestMode && updatingInputs.length === 1 && updatingInputs[0][0] === 'frequency')) {
            this.fetchChartData();
        }
    }
    ngOnDestroy() {
        this.cancelInFlightRequest();
        this.destroyChart();
    }
    /**
     * Request the chart payload, dropping whatever request was already running.
     *
     * Inputs can change several times in a row -- a host switching metric and zoom in one tick --
     * and an earlier response arriving after a later one would leave the chart showing the wrong
     * parameters, so the superseded subscription is torn down before a new one starts.
     */
    fetchChartData() {
        this.cancelInFlightRequest();
        const condition = computed(() => this.targetSecurityIds().length < 1 || !this.metric() || !this.securitylistName(), ...(ngDevMode ? [{ debugName: "condition" }] : []));
        if (condition()) {
            this.resetChart();
            this.emitChartData();
            return;
        }
        this.isLoading = true;
        this.errorMessage = '';
        this.fetchSubscription = this.universeQuartileChartService
            .getQuartileChartData(this.buildRequestParams())
            .subscribe({
            next: (response) => {
                this.isLoading = false;
                this.chartData = response.data;
                this.chartParams = response.params;
                this.renderChart();
                this.emitChartData();
            },
            error: (error) => {
                this.isLoading = false;
                this.resetChart();
                this.errorMessage = this.getErrorMessage(error);
                this.emitChartData();
            },
        });
    }
    buildRequestParams() {
        return {
            frequency: this.frequency() ?? UniverseQuartileChartFrequency.NONE,
            includeBenchmark: this.includeBenchmark(),
            metric: this.metric(),
            securityGroup: this.securityGroup() ?? '',
            securitylistName: this.securitylistName(),
            targetSecurities: this.targetSecurityIds().join(','),
            zoom: this.zoom(),
        };
    }
    /**
     * A 400 or a 413 carries a `message` written for the user -- an over-fine frequency, a
     * universe too large to summarize. Those are worth showing verbatim; anything else is not.
     */
    getErrorMessage(error) {
        const isActionable = error?.status === 400 || error?.status === 413;
        return (isActionable && error?.error?.message) || GENERIC_ERROR_MESSAGE;
    }
    renderChart() {
        this.destroyChart();
        if (!this.chartData || this.isEmpty) {
            return;
        }
        // The container only enters the DOM once the loading and error states are gone, so let
        // the template catch up before Highcharts is handed an element to render into.
        this.changeDetector.detectChanges();
        const chartElement = this.chartElement();
        if (!chartElement)
            return;
        this.chart = new Highcharts.Chart(chartElement.nativeElement, this.buildChartOptions());
    }
    buildChartOptions() {
        const { metric } = this.chartData;
        const defaultXAxis = HIGHCHARTS_UNIVERSE_COLUMNRANGE_DEFAULT_OPTIONS.xAxis;
        const defaultYAxis = HIGHCHARTS_UNIVERSE_COLUMNRANGE_DEFAULT_OPTIONS.yAxis;
        const component = this;
        return {
            ...HIGHCHARTS_UNIVERSE_COLUMNRANGE_DEFAULT_OPTIONS,
            xAxis: {
                ...defaultXAxis,
                type: 'category',
                categories: this.buildChartCategories(),
            },
            yAxis: {
                ...defaultYAxis,
                title: metric.label ? { text: metric.label } : defaultYAxis.title,
            },
            tooltip: {
                ...HIGHCHARTS_UNIVERSE_COLUMNRANGE_DEFAULT_OPTIONS.tooltip,
                formatter() {
                    const { point } = this;
                    const metricType = component.chartData?.metric?.type;
                    const rows = point.series.chart.series.map((series) => {
                        const item = series.points.find((candidate) => candidate.x === point.x);
                        if (!item) {
                            return '';
                        }
                        const value = series.type === 'scatter'
                            ? formatValue(item.y, metricType)
                            : `${formatValue(item.low, metricType)}–${formatValue(item.high, metricType)}`;
                        return tooltipRow(series.name, value, series.options?.custom?.bulletClass);
                    });
                    const header = `<div class="hightcharts-header">${point.category}</div>`;
                    return `<div class="highcharts-tooltip-inner">${header}${rows.join('')}</div>`;
                },
            },
            series: this.buildSeries(),
            ...this.chartOptions(),
        };
    }
    buildSeries() {
        const { universe, targets, benchmark } = this.chartData;
        const slots = this.periodSlots();
        const series = [...this.buildUniverseSeries(universe.boxes, slots)];
        targets.forEach((target, index) => {
            series.push(this.buildTargetSeries(target, `${TARGET_SERIES_ID_PREFIX}${index}`, this.targetColor(index), slots, `bullet-${TARGET_COLOR_INDICES[index % TARGET_COLOR_INDICES.length]}`));
        });
        if (benchmark) {
            series.push(this.buildBenchmarkSeries(benchmark, slots));
        }
        return series;
    }
    /**
     * Which axis slot each period sits in, keyed by the date the endpoint dated it.
     *
     * The axis is categorical, so a point's `x` is a slot number rather than a date. Numbering
     * each series' own points would put them in the wrong slots: the endpoint leaves out a period
     * a security has no value for, so a security missing an early period would have every later
     * value shifted left onto the wrong box. The universe's boxes are the periods, so they are
     * what defines the slots -- and `buildChartCategories` labels the same boxes in the same
     * order, which keeps slots and labels in step.
     */
    periodSlots() {
        const boxes = this.chartData?.universe.boxes ?? [];
        return new Map(boxes.map((box, index) => [box.date, index]));
    }
    buildChartCategories() {
        const frequency = this.frequency();
        const boxes = this.chartData?.universe.boxes ?? [];
        if (this.isLatestMode) {
            // The single period is labelled with its year. A box the endpoint dated with
            // something unparseable leaves nothing to take a year from -- and `formatDate` would
            // hand back the string "NaN" rather than nothing -- so the generic label stands in.
            const timestamps = boxes.map((box) => Date.parse(box.date)).filter((time) => !Number.isNaN(time));
            if (!timestamps.length) {
                return [LATEST_CATEGORY_LABEL];
            }
            return [formatDate(Math.max(...timestamps), UniverseQuartileChartFrequency.YEARLY)];
        }
        return boxes.map((box) => formatDate(box.date, frequency));
    }
    /**
     * The universe drawn as contiguous bands, one series per band.
     *
     * `grouping: false` puts them all in the same slot, so the four stack into a single column per
     * period. They are separate series rather than one because a `columnrange` point carries only
     * a low and a high, and each band needs its own fill.
     */
    buildUniverseSeries(boxes, slots) {
        return UNIVERSE_BANDS.map((band) => ({
            type: 'columnrange',
            id: band.id,
            name: band.name,
            color: band.color,
            custom: { bulletClass: `bullet-${band.bulletColor}` },
            data: boxes.flatMap((box) => {
                const low = box[band.from];
                const high = box[band.to];
                const x = slots.get(box.date);
                // A percentile the universe could not compute leaves this band with no extent to
                // draw. The bands around it still render, so the column shows what is known.
                if (low === null || high === null || x === undefined)
                    return [];
                return [
                    {
                        x,
                        low,
                        high,
                        custom: {
                            count: box.count.toFixed(2),
                            p05: box.low ? box.low.toFixed(2) : null,
                            q1: box.q1 ? box.q1.toFixed(2) : null,
                            median: box.median ? box.median.toFixed(2) : null,
                            q3: box.q3 ? box.q3.toFixed(2) : null,
                            p95: box.high ? box.high.toFixed(2) : null,
                        },
                    },
                ];
            }),
        }));
    }
    buildTargetSeries(target, id, color, slots, bulletClass) {
        return {
            type: 'scatter',
            id,
            name: target.name,
            color,
            custom: {
                bulletClass: bulletClass ?? '',
            },
            data: target.data.flatMap((point) => {
                const x = slots.get(point.date);
                // A value the universe has no period for has no box to be read against. Both are
                // resampled onto the same grid, so this should not happen -- and if it does, the
                // honest thing is to leave the point out rather than place it against the wrong
                // period.
                if (x === undefined)
                    return [];
                return [
                    {
                        x,
                        y: point.value,
                        // A security outside the selected list holds no rank inside it, and the
                        // benchmark never does. Formatting a missing percentile as a number would
                        // read as the most extreme rank there is rather than as no rank at all.
                        custom: {
                            percentile: point.percentile !== null && target.inUniverse
                                ? `P${point.percentile.toFixed(0)}`
                                : NOT_RANKED_LABEL,
                        },
                    },
                ];
            }),
        };
    }
    buildBenchmarkSeries(benchmark, slots) {
        return this.buildTargetSeries(benchmark, BENCHMARK_SERIES_ID, BENCHMARK_COLOR, slots, 'bullet-2');
    }
    targetColor(index) {
        return TARGET_COLORS[index % TARGET_COLORS.length];
    }
    cancelInFlightRequest() {
        if (this.fetchSubscription !== null) {
            this.fetchSubscription.unsubscribe();
            this.fetchSubscription = null;
        }
    }
    destroyChart() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }
    resetChart() {
        this.destroyChart();
        this.chartData = null;
    }
    emitChartData() {
        this.chartDataHandler.emit({
            metric: this.chartData?.metric ?? null,
            params: this.chartParams,
            isEmpty: this.isEmpty,
            errorMessage: this.errorMessage,
        });
    }
}
UniverseQuartileChartComponent.ɵfac = function UniverseQuartileChartComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || UniverseQuartileChartComponent)(); };
UniverseQuartileChartComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: UniverseQuartileChartComponent, selectors: [["ycn-universe-quartile-chart"]], viewQuery: function UniverseQuartileChartComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuerySignal(ctx.chartElement, _c0, 5);
    } if (rf & 2) {
        i0.ɵɵqueryAdvance();
    } }, inputs: { targetSecurities: [1, "targetSecurities"], metric: [1, "metric"], securitylistName: [1, "securitylistName"], securityGroup: [1, "securityGroup"], zoom: [1, "zoom"], frequency: [1, "frequency"], includeBenchmark: [1, "includeBenchmark"], chartId: [1, "chartId"], noDataHtml: [1, "noDataHtml"], chartOptions: [1, "chartOptions"] }, outputs: { chartDataHandler: "chartDataHandler" }, standalone: false, features: [i0.ɵɵNgOnChangesFeature], decls: 5, vars: 1, consts: [["chart", ""], [1, "universe-quartile-chart", "min-height-200"], ["data-test-id", "quartile-chart-loader", 1, "loader"], ["data-test-id", "quartile-chart-error", 1, "panel-data-empty-message", "text-prewrap"], ["data-test-id", "quartile-chart-no-data", 1, "panel-data-empty-message", "text-prewrap"], ["data-test-id", "quartile-chart-canvas", 1, "highcharts-container", 3, "id"], [1, "loader-message"], [1, "loader-spinner"]], template: function UniverseQuartileChartComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 1);
        i0.ɵɵconditionalCreate(1, UniverseQuartileChartComponent_Conditional_1_Template, 4, 0, "div", 2)(2, UniverseQuartileChartComponent_Conditional_2_Template, 2, 1, "div", 3)(3, UniverseQuartileChartComponent_Conditional_3_Template, 2, 1, "div", 4)(4, UniverseQuartileChartComponent_Conditional_4_Template, 2, 1, "div", 5);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.isLoading ? 1 : ctx.errorMessage ? 2 : ctx.isEmpty ? 3 : 4);
    } }, encapsulation: 2 });
